import React, { useState, useEffect } from 'react';
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Calendar,
  MapPin,
  User,
  Phone,
  CreditCard,
  FileText,
  Download
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { contractService } from '../../services/contractService';
import { Contract, EnderecoViaCEP, FormErrors } from '../../types/contract';
import { formatDate, toISOString } from '../../utils/dateUtils';
import { PageTransition } from '../ui/PageTransition';
import { PageTitle } from '../ui/PageTitle';
import { useContracts } from '../../contexts/ContractContext';
import { validateCPF, validateCNPJ, getDocumentLabel, applyDocumentMask, detectDocumentType, limitDocumentInput, applyCPFMask, applyCNPJMask } from '../../utils/documentUtils';

const EditContract: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { updateContract } = useContracts();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true); // Inicia como true para carregar o contrato
  
  const [contrato, setContrato] = useState<Partial<Contract>>({
    status: 'orcamento',
    tipoDocumento: 'cpf',
    createdAt: new Date()
  });

  const [errors, setErrors] = useState<Partial<FormErrors>>({});

  useEffect(() => {
    const loadContract = async () => {
      if (id) {
        try {
          setLoading(true);
          const fetchedContract = await contractService.getContractById(id);
          if (fetchedContract) {
            // Formatar a data para o input type="date" (formato ISO)
            if (fetchedContract.dataFesta) {
              fetchedContract.dataFesta = toISOString(fetchedContract.dataFesta);
            }
            
            // Aplicar máscara nos documentos se existirem
            if (fetchedContract.cpf) {
              fetchedContract.cpf = applyCPFMask(fetchedContract.cpf);
            }
            if (fetchedContract.cnpj) {
              fetchedContract.cnpj = applyCNPJMask(fetchedContract.cnpj);
            }
            
            // Se não tem tipoDocumento definido, detectar baseado no que existe
            if (!fetchedContract.tipoDocumento) {
              if (fetchedContract.cpf) {
                fetchedContract.tipoDocumento = 'cpf';
              } else if (fetchedContract.cnpj) {
                fetchedContract.tipoDocumento = 'cnpj';
              } else {
                fetchedContract.tipoDocumento = 'cpf'; // padrão
              }
            }
            
            setContrato(fetchedContract);
          } else {
            // Contrato não encontrado, redirecionar ou mostrar erro
            navigate('/admin/contratos');
          }
        } catch (error) {
          console.error('Erro ao carregar contrato para edição:', error);
          navigate('/admin/contratos');
        } finally {
          setLoading(false);
        }
      }
    };
    loadContract();
  }, [id, navigate]);

  // Validação de CPF
  const validateCPF = (cpf: string): boolean => {
    const cleanCPF = cpf.replace(/\D/g, '');
    if (cleanCPF.length !== 11) return false;
    
    // Verificar se todos os dígitos são iguais
    if (/^(\d)\1+$/.test(cleanCPF)) return false;
    
    // Validar primeiro dígito verificador
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cleanCPF.charAt(i)) * (10 - i);
    }
    let remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cleanCPF.charAt(9))) return false;
    
    // Validar segundo d��gito verificador
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cleanCPF.charAt(i)) * (11 - i);
    }
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cleanCPF.charAt(10))) return false;
    
    return true;
  };

  // Validação de telefone
  const validatePhone = (phone: string): boolean => {
    const cleanPhone = phone.replace(/\D/g, '');
    return cleanPhone.length >= 10 && cleanPhone.length <= 11;
  };

  // Buscar CEP
  const buscarCEP = async (cep: string) => {
    if (cep.length !== 8) return;
    
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data: EnderecoViaCEP = await response.json();
      
      if (!data.erro) {
        setContrato(prev => ({
          ...prev,
          endereco: data.logradouro,
          bairro: data.bairro,
          cidade: data.localidade,
          estado: data.uf
        }));
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
    }
  };

  // Validação do passo atual
  const validateStep = (): boolean => {
    const newErrors: Partial<FormErrors> = {};

    switch (currentStep) {
      case 1:
        if (!contrato.contratante?.trim()) newErrors.contratante = 'Nome é obrigatório';
        
        // Validação do documento baseada no tipo selecionado
        if (contrato.tipoDocumento === 'cpf') {
          if (!contrato.cpf?.trim()) {
            newErrors.cpf = 'CPF é obrigatório';
          } else if (!validateCPF(contrato.cpf)) {
            newErrors.cpf = 'CPF inválido';
          }
        } else if (contrato.tipoDocumento === 'cnpj') {
          if (!contrato.cnpj?.trim()) {
            newErrors.cnpj = 'CNPJ é obrigatório';
          } else if (!validateCNPJ(contrato.cnpj)) {
            newErrors.cnpj = 'CNPJ inválido';
          }
        }
        
        if (!contrato.telefone?.trim()) {
          newErrors.telefone = 'Telefone é obrigatório';
        } else if (!validatePhone(contrato.telefone)) {
          newErrors.telefone = 'Telefone inválido';
        }
        break;

      case 2:
        if (!contrato.nomeAniversariante?.trim()) newErrors.nomeAniversariante = 'Nome do aniversariante é obrigatório';
        if (!contrato.idadeAniversariante) newErrors.idadeAniversariante = 'Idade é obrigatória';
        if (!contrato.dataFesta) newErrors.dataFesta = 'Data da festa é obrigatória';
        if (!contrato.horarioInicioFesta?.trim()) newErrors.horarioInicioFesta = 'Horário é obrigatório';
        if (!contrato.inicioRecreacao?.trim()) newErrors.inicioRecreacao = 'Horário de início da recreação é obrigatório';
        if (!contrato.fimRecreacao?.trim()) newErrors.fimRecreacao = 'Horário de fim da recreação é obrigatório';
        break;

      case 3:
        if (!contrato.endereco?.trim()) newErrors.endereco = 'Endereço é obrigatório';
        if (!contrato.numero?.trim()) newErrors.numero = 'Número é obrigatório';
        if (!contrato.bairro?.trim()) newErrors.bairro = 'Bairro é obrigatório';
        if (!contrato.cidade?.trim()) newErrors.cidade = 'Cidade é obrigatória';
        if (!contrato.estado?.trim()) newErrors.estado = 'Estado é obrigatório';
        break;

      case 4:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Próximo passo
  const nextStep = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  // Passo anterior
  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Enviar formulário
  const handleSubmit = async () => {
    if (!validateStep()) return;

    setLoading(true);
    try {
      if (id) {
        // Atualizar no Firebase
        await contractService.updateContract(id, contrato as Contract);
        
        // Atualizar no contexto local para refletir as mudanças imediatamente
        updateContract(id, contrato as Contract);
        
        alert('Contrato atualizado com sucesso!');
        navigate(`/admin/contratos/${id}`);
      }
    } catch (error) {
      console.error('Erro ao atualizar contrato:', error);
      alert('Erro ao atualizar contrato. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PageTransition>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
            <p className="text-slate-600">Carregando contrato...</p>
          </div>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="w-full mx-auto px-2 sm:px-4 lg:px-6 overflow-x-hidden">
        {/* Header Principal */}
        <div className="mx-2 sm:mx-4 lg:mx-6 mt-2 mb-3">
          <PageTitle
            title="Editar Contrato"
            subtitle="Atualize as informações do contrato existente"
          >
            <button
              onClick={() => navigate(`/admin/contratos/${id}`)}
              className="px-4 py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 flex items-center gap-2 font-semibold border border-white/30 transition-all duration-200"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar ao Contrato
            </button>
          </PageTitle>
        </div>

        {/* Progress Steps */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200/50 p-4 sm:p-8 mb-8">
          <div className="flex items-center justify-between mb-4">
            {[1, 2, 3, 4, 5].map((step) => (
              <div key={step} className="flex items-center cursor-pointer" onClick={() => setCurrentStep(step)}>
                <div className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold transition-all duration-300 ${
                  currentStep >= step 
                    ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-lg scale-110' 
                    : 'bg-slate-200 text-slate-600'
                }`}>
                  {currentStep > step ? <Check className="w-5 h-5" /> : step}
                </div>
                {step < 5 && (
                  <div className={`hidden sm:block w-12 sm:w-20 h-1 mx-3 rounded-full transition-all duration-300 ${
                    currentStep > step 
                      ? 'bg-gradient-to-r from-teal-500 to-teal-600' 
                      : 'bg-slate-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="hidden sm:flex flex-col sm:flex-row justify-between text-sm font-medium text-slate-600 gap-2">
            <span className="text-center flex-1">Seus Dados</span>
            <span className="text-center flex-1">Evento</span>
            <span className="text-center flex-1">Local</span>
            <span className="text-center flex-1">Pacote</span>
            <span className="text-center flex-1">Confirmação</span>
          </div>
        </div>

        {/* Formulário */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200/50 p-4 sm:p-8">
          {/* Passo 1: Dados Pessoais */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-lg sm:text-2xl font-bold text-slate-900 flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                Seus Dados
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    value={contrato.contratante || ''}
                    onChange={(e) => setContrato(prev => ({ ...prev, contratante: e.target.value }))}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 ${
                      errors.contratante ? 'border-red-500 bg-red-50' : 'border-slate-300 bg-white/50'
                    }`}
                    placeholder="Nome Completo *"
                  />
                  {errors.contratante && (
                    <p className="text-red-500 text-sm mt-1">{errors.contratante}</p>
                  )}
                </div>

                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Tipo de Documento *
                  </label>
                  <select 
                    value={contrato.tipoDocumento || 'cpf'} 
                    onChange={(e) => {
                      setContrato(prev => ({ 
                        ...prev, 
                        tipoDocumento: e.target.value as 'cpf' | 'cnpj'
                      }));
                    }} 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="cpf">CPF</option>
                    <option value="cnpj">CNPJ</option>
                  </select>
                </div>
                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    {getDocumentLabel(contrato.tipoDocumento || 'cpf')} *
                  </label>
                  <input
                    type="text"
                    value={contrato.tipoDocumento === 'cnpj' ? (contrato.cnpj || '') : (contrato.cpf || '')}
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      const cleanValue = limitDocumentInput(rawValue, contrato.tipoDocumento || 'cpf');
                      const formattedValue = applyDocumentMask(cleanValue, contrato.tipoDocumento || 'cpf');
                      
                      // Apenas formatação baseada no tipo selecionado
                      if (contrato.tipoDocumento === 'cnpj') {
                        setContrato(prev => ({ ...prev, cnpj: formattedValue }));
                      } else {
                        setContrato(prev => ({ ...prev, cpf: formattedValue }));
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                      (errors.cpf || errors.cnpj) ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder={`${getDocumentLabel(contrato.tipoDocumento || 'cpf')} *`}
                    maxLength={contrato.tipoDocumento === 'cnpj' ? 18 : 14}
                  />
                  {errors.cpf && (
                    <p className="text-red-500 text-sm mt-1">{errors.cpf}</p>
                  )}
                  {errors.cnpj && (
                    <p className="text-red-500 text-sm mt-1">{errors.cnpj}</p>
                  )}
                </div>

                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Telefone *
                  </label>
                  <input
                    type="text"
                    value={contrato.telefone || ''}
                    onChange={(e) => setContrato(prev => ({ ...prev, telefone: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                      errors.telefone ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Telefone *"
                  />
                  {errors.telefone && (
                    <p className="text-red-500 text-sm mt-1">{errors.telefone}</p>
                  )}
                </div>

                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={contrato.email || ''}
                    onChange={(e) => setContrato(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Email"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Passo 2: Dados da Festa */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Calendar className="w-6 h-6 text-primary" />
                Dados da Festa
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Nome do Aniversariante *
                  </label>
                  <input
                    type="text"
                    value={contrato.nomeAniversariante || ''}
                    onChange={(e) => setContrato(prev => ({ ...prev, nomeAniversariante: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                      errors.nomeAniversariante ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Nome do Aniversariante *"
                  />
                  {errors.nomeAniversariante && (
                    <p className="text-red-500 text-sm mt-1">{errors.nomeAniversariante}</p>
                  )}
                </div>

                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Idade *
                  </label>
                  <input
                    type="number"
                    value={contrato.idadeAniversariante || ''}
                    onChange={(e) => setContrato(prev => ({ ...prev, idadeAniversariante: parseInt(e.target.value) }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                      errors.idadeAniversariante ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Idade *"
                    min="1"
                    max="100"
                  />
                  {errors.idadeAniversariante && (
                    <p className="text-red-500 text-sm mt-1">{errors.idadeAniversariante}</p>
                  )}
                </div>

                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Data da Festa *
                  </label>
                  <input
                    type="date"
                    value={contrato.dataFesta || ''}
                    onChange={(e) => setContrato(prev => ({ ...prev, dataFesta: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                      errors.dataFesta ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.dataFesta && (
                    <p className="text-red-500 text-sm mt-1">{errors.dataFesta}</p>
                  )}
                </div>

                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Horário de Início *
                  </label>
                  <input
                    type="time"
                    value={contrato.horarioInicioFesta || ''}
                    onChange={(e) => setContrato(prev => ({ ...prev, horarioInicioFesta: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                      errors.horarioInicioFesta ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.horarioInicioFesta && (
                    <p className="text-red-500 text-sm mt-1">{errors.horarioInicioFesta}</p>
                  )}
                </div>

                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Tema da Festa
                  </label>
                  <input
                    type="text"
                    value={contrato.temaFesta || ''}
                    onChange={(e) => setContrato(prev => ({ ...prev, temaFesta: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Tema da Festa"
                  />
                </div>

                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Número de Convidados
                  </label>
                  <input
                    type="number"
                    value={contrato.numeroConvidados || ''}
                    onChange={(e) => setContrato(prev => ({ ...prev, numeroConvidados: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Número de Convidados"
                    min="1"
                  />
                </div>

                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Horário de Início da Recreação *
                  </label>
                  <input
                    type="time"
                    value={contrato.inicioRecreacao || ''}
                    onChange={(e) => setContrato(prev => ({ ...prev, inicioRecreacao: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                      errors.inicioRecreacao ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.inicioRecreacao && (
                    <p className="text-red-500 text-sm mt-1">{errors.inicioRecreacao}</p>
                  )}
                </div>

                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Horário de Fim da Recreação *
                  </label>
                  <input
                    type="time"
                    value={contrato.fimRecreacao || ''}
                    onChange={(e) => setContrato(prev => ({ ...prev, fimRecreacao: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                      errors.fimRecreacao ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.fimRecreacao && (
                    <p className="text-red-500 text-sm mt-1">{errors.fimRecreacao}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Passo 3: Endereço */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <MapPin className="w-6 h-6 text-primary" />
                Endereço da Festa
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    CEP
                  </label>
                  <input
                    type="text"
                    value={contrato.cep || ''}
                    onChange={(e) => {
                      const cep = e.target.value.replace(/\D/g, '');
                      setContrato(prev => ({ ...prev, cep }));
                      if (cep.length === 8) {
                        buscarCEP(cep);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="CEP"
                    maxLength={9}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Endereço *
                  </label>
                  <input
                    type="text"
                    value={contrato.endereco || ''}
                    onChange={(e) => setContrato(prev => ({ ...prev, endereco: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                      errors.endereco ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Endereço *"
                  />
                  {errors.endereco && (
                    <p className="text-red-500 text-sm mt-1">{errors.endereco}</p>
                  )}
                </div>

                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Número *
                  </label>
                  <input
                    type="text"
                    value={contrato.numero || ''}
                    onChange={(e) => setContrato(prev => ({ ...prev, numero: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                      errors.numero ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Número *"
                  />
                  {errors.numero && (
                    <p className="text-red-500 text-sm mt-1">{errors.numero}</p>
                  )}
                </div>

                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Complemento
                  </label>
                  <input
                    type="text"
                    value={contrato.complemento || ''}
                    onChange={(e) => setContrato(prev => ({ ...prev, complemento: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Complemento"
                  />
                </div>

                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Bairro *
                  </label>
                  <input
                    type="text"
                    value={contrato.bairro || ''}
                    onChange={(e) => setContrato(prev => ({ ...prev, bairro: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                      errors.bairro ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Bairro *"
                  />
                  {errors.bairro && (
                    <p className="text-red-500 text-sm mt-1">{errors.bairro}</p>
                  )}
                </div>

                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Cidade *
                  </label>
                  <input
                    type="text"
                    value={contrato.cidade || ''}
                    onChange={(e) => setContrato(prev => ({ ...prev, cidade: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                      errors.cidade ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Cidade *"
                  />
                  {errors.cidade && (
                    <p className="text-red-500 text-sm mt-1">{errors.cidade}</p>
                  )}
                </div>

                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Estado *
                  </label>
                  <select
                    value={contrato.estado || ''}
                    onChange={(e) => setContrato(prev => ({ ...prev, estado: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                      errors.estado ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Selecione o Estado *</option>
                    <option value="AC">Acre</option>
                    <option value="AL">Alagoas</option>
                    <option value="AP">Amapá</option>
                    <option value="AM">Amazonas</option>
                    <option value="BA">Bahia</option>
                    <option value="CE">Ceará</option>
                    <option value="DF">Distrito Federal</option>
                    <option value="ES">Espírito Santo</option>
                    <option value="GO">Goiás</option>
                    <option value="MA">Maranhão</option>
                    <option value="MT">Mato Grosso</option>
                    <option value="MS">Mato Grosso do Sul</option>
                    <option value="MG">Minas Gerais</option>
                    <option value="PA">Pará</option>
                    <option value="PB">Paraíba</option>
                    <option value="PR">Paraná</option>
                    <option value="PE">Pernambuco</option>
                    <option value="PI">Piauí</option>
                    <option value="RJ">Rio de Janeiro</option>
                    <option value="RN">Rio Grande do Norte</option>
                    <option value="RS">Rio Grande do Sul</option>
                    <option value="RO">Rondônia</option>
                    <option value="RR">Roraima</option>
                    <option value="SC">Santa Catarina</option>
                    <option value="SP">São Paulo</option>
                    <option value="SE">Sergipe</option>
                    <option value="TO">Tocantins</option>
                  </select>
                  {errors.estado && (
                    <p className="text-red-500 text-sm mt-1">{errors.estado}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Passo 4: Serviços */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="w-6 h-6 text-primary" />
                Serviços e Atividades
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Forma de Pagamento
                  </label>
                  <select
                    value={contrato.formaPagamento || ''}
                    onChange={(e) => setContrato(prev => ({ ...prev, formaPagamento: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">Forma de Pagamento</option>
                    <option value="PIX">PIX</option>
                    <option value="Cartão de Crédito">Cartão de Crédito</option>
                    <option value="Cartão de Débito">Cartão de Débito</option>
                    <option value="Dinheiro">Dinheiro</option>
                    <option value="Transferência">Transferência</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="hidden sm:block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Observações
                  </label>
                  <textarea
                    value={contrato.observacoes || ''}
                    onChange={(e) => setContrato(prev => ({ ...prev, observacoes: e.target.value }))}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Observações"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Passo 5: Revisão */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Check className="w-6 h-6 text-primary" />
                Revisão dos Dados
              </h2>
              
              <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Dados do Contratante</h3>
                    <p><strong>Nome:</strong> {contrato.contratante}</p>
                    <p><strong>{getDocumentLabel(contrato.tipoDocumento || 'cpf')}:</strong> {contrato.tipoDocumento === 'cnpj' ? contrato.cnpj : contrato.cpf}</p>
                    <p><strong>Telefone:</strong> {contrato.telefone}</p>
                    <p><strong>Email:</strong> {contrato.email || 'Não informado'}</p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Dados da Festa</h3>
                    <p><strong>Aniversariante:</strong> {contrato.nomeAniversariante}</p>
                    <p><strong>Idade:</strong> {contrato.idadeAniversariante} anos</p>
                    <p><strong>Data:</strong> {contrato.dataFesta ? formatDate(contrato.dataFesta) : 'Não informada'}</p>
                    <p><strong>Horário:</strong> {contrato.horarioInicioFesta}</p>
                    <p><strong>Tema:</strong> {contrato.temaFesta || 'Não informado'}</p>
                    <p><strong>Convidados:</strong> {contrato.numeroConvidados || 'Não informado'}</p>
                    <p><strong>Início da Recreação:</strong> {contrato.inicioRecreacao || 'Não informado'}</p>
                    <p><strong>Fim da Recreação:</strong> {contrato.fimRecreacao || 'Não informado'}</p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Endereço</h3>
                    <p><strong>Endereço:</strong> {contrato.endereco}, {contrato.numero}</p>
                    <p><strong>Complemento:</strong> {contrato.complemento || 'Não informado'}</p>
                    <p><strong>Bairro:</strong> {contrato.bairro}</p>
                    <p><strong>Cidade:</strong> {contrato.cidade} - {contrato.estado}</p>
                    <p><strong>CEP:</strong> {contrato.cep || 'Não informado'}</p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Serviços</h3>
                    <p><strong>Forma de Pagamento:</strong> {contrato.formaPagamento || 'Não informada'}</p>
                    <p><strong>Observações:</strong> {contrato.observacoes || 'Nenhuma'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navegação */}
          <div className="flex justify-between mt-8">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Anterior</span>
            </button>

            {currentStep < 5 ? (
              <button
                onClick={nextStep}
                className="flex items-center gap-2 px-6 py-3 text-lg font-semibold bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transform transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <span className="hidden sm:inline">Próximo</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Atualizando...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    Atualizar Contrato
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        
      </div>
    </PageTransition>
  );
};

export default EditContract;
